import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Controller } from 'react-hook-form';
import { FormControl, FormHelperText, FormLabel, OutlinedInput, makeStyles } from '@material-ui/core';
import { z } from 'zod';
import ChipList from '../Common/Form/ChipList';
import Chip from '../Common/Form/Chip';
import Form from '../Common/Form';
import { AUTOMATION_SPIDER_EXCLUSIONS } from '../../helpers/constants';

const REMOVE_FROM_SPOODER = 'Remove from Spooder';
const EXCLUSION_TYPES = AUTOMATION_SPIDER_EXCLUSIONS.filter(e => e !== REMOVE_FROM_SPOODER);

const validationSchema = () => {
  return z.object({
    spider_exclusion: z
      .string()
      .nullable()
      .catch(null),
    reason: z
      .string()
      .trim()
      .nonempty(),
  });
};

const DEFAULT_FORM_VALUES = {
  spider_exclusion: '',
  reason: '',
};

const useStyles = makeStyles(theme => ({
  required: {
    color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
  },
  label: {
    marginBottom: theme.spacing(2),
    fontSize: '14px',
  },
  helperText: {
    fontSize: '14px',
    lineHeight: '1.2',
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(0.625),
  },
  removeFromSpooderContainer: {
    marginTop: '12px',
  },
}));

const propTypes = {
  automationData: PropTypes.shape({
    spider_exclusion: PropTypes.string,
    reason: PropTypes.string,
  }),
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSuccess: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  eventMeta: PropTypes.object,
};
const defaultProps = {
  automationData: null,
  isError: false,
  isLoading: false,
  isSuccess: false,
  onReset: undefined,
  eventMeta: undefined,
};

function parseSpiderExclusion(value) {
  if (!value) return { exclusionType: '', removeFromSpooder: false };
  const parts = value.split(',').map(p => p.trim());
  const removeFromSpooder = parts.includes(REMOVE_FROM_SPOODER);
  const exclusionType = parts.find(p => p !== REMOVE_FROM_SPOODER) || '';
  return { exclusionType, removeFromSpooder };
}

function buildSpiderExclusion(exclusionType, removeFromSpooder) {
  const parts = [];
  if (exclusionType) parts.push(exclusionType);
  if (removeFromSpooder) parts.push(REMOVE_FROM_SPOODER);
  return parts.join(',') || '';
}

function SpiderForm({ automationData, isError, isLoading, isSuccess, onSubmit, onReset, eventMeta }) {
  const classes = useStyles();

  const initialValues = useMemo(() => {
    if (!automationData) {
      return DEFAULT_FORM_VALUES;
    }

    return {
      spider_exclusion: automationData.spider_exclusion || '',
      reason: '',
    };
  }, [automationData]);

  return (
    <Form
      initialValues={initialValues}
      isError={isError}
      isLoading={isLoading}
      isSuccess={isSuccess}
      validationSchema={validationSchema(eventMeta.category)}
      values={initialValues}
      onSubmit={onSubmit}
      onReset={onReset}
    >
      {({ control }) => (
        <>
          <FormControl component="fieldset">
            <FormLabel component="legend" classes={{ root: classes.label }}>
              Please select the exclusion type. (Optional)
            </FormLabel>

            <Controller
              name="spider_exclusion"
              control={control}
              render={({ field }) => {
                const { exclusionType, removeFromSpooder } = parseSpiderExclusion(field.value);

                const handleExclusionChange = newExclusionType => {
                  field.onChange(buildSpiderExclusion(newExclusionType, removeFromSpooder));
                };

                const handleExclusionDelete = () => {
                  field.onChange(buildSpiderExclusion('', removeFromSpooder));
                };

                const handleRemoveFromSpooderToggle = () => {
                  field.onChange(buildSpiderExclusion(exclusionType, !removeFromSpooder));
                };

                return (
                  <>
                    <ChipList
                      disabled={field.disabled}
                      options={EXCLUSION_TYPES.map(option => ({ label: option, value: option }))}
                      value={exclusionType}
                      onChange={val => (val === exclusionType ? handleExclusionDelete() : handleExclusionChange(val))}
                    />
                    <div className={classes.removeFromSpooderContainer}>
                      <Chip
                        disabled={field.disabled}
                        checked={removeFromSpooder}
                        label={REMOVE_FROM_SPOODER}
                        value={REMOVE_FROM_SPOODER}
                        onClick={handleRemoveFromSpooderToggle}
                        onDelete={handleRemoveFromSpooderToggle}
                      />
                    </div>
                  </>
                );
              }}
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel classes={{ root: classes.label }}>
              Please provide a reason for this request.
              <span className={classes.required}>* </span>
              (Required)
            </FormLabel>
            <FormHelperText className={classes.helperText}>
              Previous reason:
              <br />
              {automationData?.reason}
            </FormHelperText>

            <Controller
              name="reason"
              control={control}
              render={({ field }) => <OutlinedInput placeholder="Type text here." rows={3} multiline {...field} />}
            />
          </FormControl>
        </>
      )}
    </Form>
  );
}

SpiderForm.propTypes = propTypes;
SpiderForm.defaultProps = defaultProps;

function mapStateToProps(state) {
  return {
    eventMeta: state.pricing.eventMeta,
  };
}

export default connect(mapStateToProps)(SpiderForm);
